import { useState } from 'react'
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage'
import { firebaseApp } from './firebase'

export interface UploadedFile {
  url: string
  name: string
  storage_path: string
  content_type: string
}

/* Same pattern as miner/Kyc.tsx: the browser uploads straight to Firebase
 * Storage and only the resulting download URL reaches the Flask API - reused
 * here because permits/documents/training-proof/daily-safety-report all need
 * it too. */
export function useStorageUpload(pathPrefix: string, maxBytes = 15 * 1024 * 1024) {
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState('')

  function upload(file: File): Promise<UploadedFile> {
    setError('')
    if (file.size > maxBytes) {
      const msg = `That file is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`
      setError(msg)
      return Promise.reject(new Error(msg))
    }
    const ext = file.name.includes('.') ? file.name.split('.').pop() : 'bin'
    const storagePath = `${pathPrefix}/${crypto.randomUUID()}.${ext}`
    const task = uploadBytesResumable(ref(getStorage(firebaseApp), storagePath), file, {
      contentType: file.type || 'application/octet-stream',
    })
    setProgress(0)
    return new Promise((resolve, reject) => {
      task.on('state_changed',
        (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err) => {
          setProgress(null)
          const msg = err.code === 'storage/unauthorized'
            ? 'You do not have permission to upload. Contact your administrator.'
            : `Upload failed: ${err.message}`
          setError(msg)
          reject(err)
        },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref)
          setProgress(null)
          resolve({ url, name: file.name, storage_path: storagePath, content_type: file.type || '' })
        },
      )
    })
  }

  return { upload, progress, error, setError }
}

/** Spread an UploadedFile into the {prefix}_url / _name / _storage_path /
 * _content_type fields the API's _file_meta() helper reads. */
export function uploadedFileFields(file: UploadedFile | null, prefix: string): Record<string, string> {
  if (!file) return {}
  return {
    [`${prefix}_url`]: file.url,
    [`${prefix}_name`]: file.name,
    [`${prefix}_storage_path`]: file.storage_path,
    [`${prefix}_content_type`]: file.content_type,
  }
}
